import { Trans } from "react-i18next";
import React, { useEffect } from "react";
import "@shared/styles/keyboard-shortcuts.css";

export interface ShortcutConfig {
  key: string;
  description: string;
  action: () => void;
  category: "Navigation" | "Patient" | "EEG" | "General";
}

interface KeyboardShortcutsProps {
  shortcuts: ShortcutConfig[];
  enabled?: boolean;
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  shortcuts,
  enabled = true,
}) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      shortcuts.forEach((shortcut) => {
        const keys = shortcut.key.toLowerCase().split("+");
        const hasCtrl = keys.includes("ctrl") || keys.includes("control");
        const hasShift = keys.includes("shift");
        const hasAlt = keys.includes("alt");
        const mainKey = keys[keys.length - 1];

        const keyPressed = e.key.toLowerCase();
        const ctrlMatch = hasCtrl
          ? e.ctrlKey || e.metaKey
          : !e.ctrlKey && !e.metaKey;
        const shiftMatch = hasShift ? e.shiftKey : !e.shiftKey;
        const altMatch = hasAlt ? e.altKey : !e.altKey;
        const keyMatch = keyPressed === mainKey;

        if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
          e.preventDefault();
          shortcut.action();
        }
      });
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [shortcuts, enabled]);

  return null;
};

export const ShortcutHelpPanel: React.FC<{ shortcuts: ShortcutConfig[] }> = ({
  shortcuts,
}) => {
  const categories = ["Navigation", "Patient", "EEG", "General"] as const;

  const formatKey = (key: string) => {
    return key.split("+").map((k, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <span className="key-separator">+</span>}
        <span className="keyboard-key">
          {k}
        </span>
      </React.Fragment>
    ));
  };

  return (
    <div
      className="keyboard-shortcuts-panel"
      role="dialog"
      aria-labelledby="shortcuts-title"
    >
      <h4 id="shortcuts-title" className="mb-4">
        <Trans ns="features" i18nKey="ui.copy.b465751c05fb" /></h4>
      {categories.map((category) => {
        const categoryShortcuts = shortcuts.filter(
          (s) => s.category === category,
        );
        if (categoryShortcuts.length === 0) return null;

        return (
          <div key={category} className="shortcut-category mb-4">
            <h5 className="category-title">{category}</h5>
            <div className="shortcuts-list">
              {categoryShortcuts.map((shortcut, idx) => (
                <div key={idx} className="shortcut-item">
                  <div
                    className="shortcut-keys"
                    aria-label={`Shortcut: ${shortcut.key}`}
                  >
                    {formatKey(shortcut.key)}
                  </div>
                  <div className="shortcut-description">
                    {shortcut.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="shortcuts-footer text-muted">
        <small>
          <Trans ns="features" i18nKey="ui.copy.ea683ad61606" /><span className="keyboard-key">?</span> <Trans ns="features" i18nKey="ui.copy.3a2d6e489088" /></small>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
